import Podio from "@phasesdk/api-client-for-podio";
import { HttpResponse } from "@phasesdk/api-client-for-podio/src/types/http.type";
import { SimpleObject } from "@phasesdk/api-client-for-podio/src/types/basic.type";
import { EmailDetails } from "../types/types";
import podio from "@phasesdk/api-client-for-podio";

interface PodioFileResponse {
  file_id: number;
  link: string;
  mimetype: string;
  name: string;
}

export class PodioService {
  private static appCredentials = {
    client_id: "",
    client_secret: "",
  };

  static setCredentials(clientId: string, clientSecret: string) {
    this.appCredentials.client_id = clientId;
    this.appCredentials.client_secret = clientSecret;
  }

  private static async uploadFile(
    attributes: SimpleObject,
    accessToken: string
  ): Promise<PodioFileResponse> {
    const formData = new FormData();
    formData.append("filename", attributes.filename);
    formData.append("source", attributes.emailBlob);

    const response = await fetch("https://api.podio.com/file/", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      console.log("Error uploading files:", response);
      throw new Error(`Failed to upload file: ${response.status}`);
    }
    return response.json();
  }

  private static sanitizeFilename(name: string): string {
    return name.replace(/[^a-zA-Z0-9-_\.]/g, "_").substring(0, 255);
  }

  private static getMimeTypeFromExtension(filename: string): string {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    const mimeTypes: { [key: string]: string } = {
      'txt': 'text/plain',
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'ppt': 'application/vnd.ms-powerpoint',
      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'zip': 'application/zip',
      'rar': 'application/x-rar-compressed',
      'eml': 'message/rfc822'
    };
    return mimeTypes[ext] || 'application/octet-stream';
  }

  private static async getAttachmentContent(attachment: Office.AttachmentDetails): Promise<Blob> {
    return new Promise((resolve, reject) => {
      Office.context.mailbox.item?.getAttachmentContentAsync(attachment.id, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded && result.value) {
          // Convert base64 string to binary data
          const base64Content = result.value.content;
          const binaryString = window.atob(base64Content);
          const bytes = new Uint8Array(binaryString.length);
          
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          const mimeType = this.getMimeTypeFromExtension(attachment.name);
          const blob = new Blob([bytes], { type: mimeType });
          resolve(blob);
        } else {
          reject(new Error(`Failed to get attachment content: ${result.error?.message || 'Unknown error'}`));
        }
      });
    });
  }

  private static createBlobFromString(base64Content: string, mimeType: string): Blob {
    const binaryString = window.atob(base64Content);
    const bytes = new Uint8Array(binaryString.length);
    
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return new Blob([bytes], { type: mimeType });
  }

  static normalizeInternetMessageId(messageId: string | undefined): string | undefined {
    if (!messageId) {
      return undefined;
    }

    // Trim whitespace and remove angle brackets if present
    let normalized = messageId.trim().replace(/^<+|>+$/g, '');
    
    // Split by @ to separate identifier and domain
    const parts = normalized.split('@');
    if (parts.length === 2) {
      const identifier = parts[0].trim();
      const domain = parts[1].trim();
      
      // Get only the first part of the domain (before first dot)
      // This ensures uniqueness across different organizations
      // Example: AM9PR01MB8329.eurprd01.prod.exchangelabs.com -> AM9PR01MB8329
      const domainFirstPart = domain.split('.')[0];
      
      // Return identifier@firstPartOfDomain to ensure uniqueness across organizations
      return `${identifier}@${domainFirstPart}`;
    }
    
    // If no @ found or invalid format, return as is (shouldn't happen with valid Message-IDs)
    return normalized;
  }

  private static async getItemByExternalId(
    appId: number,
    externalId: string,
    auth: any
  ): Promise<{ item_id: number } | null> {
    try {
      const response = await fetch(
        `https://api.podio.com/item/app/${appId}/external_id/${encodeURIComponent(externalId)}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${auth.access_token}`,
          },
        }
      );
      if (response.status === 404) {
        // Item not found
        return null;
      }

      if (!response.ok) {
        throw new Error(`Failed to get item by external_id: ${response.status}`);
      }

      const data = await response.json();
      return { item_id: data.item_id };
    } catch (error) {
      // If it's a 404, item doesn't exist, return null
      if (error instanceof Error && error.message.includes("404")) {
        return null;
      }
      console.error("Error getting item by external_id:", error);
      return null;
    }
  }

  private static async getItemById(
    itemId: number,
    auth: any
  ): Promise<{ item_id: number; link: string } | null> {
    try {
      const response = await fetch(
        `https://api.podio.com/item/${itemId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${auth.access_token}`,
          },
        }
      );

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`Failed to get item by id: ${response.status}`);
      }

      const data = await response.json();
      return {
        item_id: data.item_id,
        link: data.link || data.url || `https://podio.com/item/${data.item_id}`
      };
    } catch (error) {
      console.error("Error getting item by id:", error);
      return null;
    }
  }

  private static async addCommentToItem(
    itemId: number,
    commentText: string,
    auth: any
  ): Promise<void> {
    try {
      const response = await fetch(
        `https://api.podio.com/comment/item/${itemId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${auth.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            value: commentText,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to add comment: ${response.status}`);
      }

      console.log("Comment added successfully to item:", itemId);
    } catch (error) {
      console.error("Error adding comment to item:", error);
      // Don't throw error - comment failure shouldn't break the main flow
    }
  }

  static async getItemLinkByExternalId(
    appId: number,
    appToken: string,
    externalId: string
  ): Promise<string | null> {
    try {
      const auth = await Podio.auth(this.appCredentials).appAuth(appId, appToken);
      
      // Get item by external_id
      const existingItem = await this.getItemByExternalId(appId, externalId, auth);
      
      if (!existingItem) {
        return null;
      }
      
      // Get the full item to get the link
      const item = await this.getItemById(existingItem.item_id, auth);
      
      return item?.link || null;
    } catch (error) {
      console.error("Error getting item link by external_id:", error);
      return null;
    }
  }

  private static extractOriginalEmailFromBody(body: string): { from?: string; fromName?: string; to?: { email: string; name: string }[] } | null {
    if (!body) return null;

    // Convert HTML to plain text for easier parsing
    let plainText = body;
    try {
      // Remove HTML tags but preserve text content
      plainText = body.replace(/<[^>]+>/g, ' ');
      // Decode HTML entities
      const tempDiv = typeof document !== 'undefined' ? document.createElement('div') : null;
      if (tempDiv) {
        tempDiv.innerHTML = body;
        plainText = tempDiv.textContent || tempDiv.innerText || plainText;
      } else {
        // Fallback: basic HTML entity decoding
        plainText = plainText
          .replace(/&nbsp;/g, ' ')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&amp;/g, '&')
          .replace(/&quot;/g, '"');
      }
    } catch (e) {
      // If HTML parsing fails, use body as-is
      plainText = body.replace(/<[^>]+>/g, ' ');
    }

    // Look for "From:" pattern - this appears in email threads
    // Pattern: "From: Name <email>" or "From: <email>"
    // Match the first occurrence which should be the original email
    const fromMatch = plainText.match(/From:\s*([^<\n]+?)\s*<([^>\n]+)>/i);
    
    // Look for "To:" pattern - get the first occurrence (original email)
    // Pattern: "To: Name <email>" or "To: email"
    const toMatch = plainText.match(/To:\s*([^<\n]+?)\s*<([^>\n]+)>/i);
    
    if (!fromMatch && !toMatch) {
      return null;
    }

    const result: { from?: string; fromName?: string; to?: { email: string; name: string }[] } = {};

    if (fromMatch) {
      result.from = fromMatch[2].trim();
      const name = fromMatch[1]?.trim();
      if (name && name.length > 0) {
        result.fromName = name;
      }
    }

    if (toMatch) {
      result.to = [{
        email: toMatch[2].trim(),
        name: toMatch[1]?.trim() || ''
      }];
    }

    return Object.keys(result).length > 0 ? result : null;
  }

  private static formatOriginalEmailComment(emailDetails: EmailDetails): string {
    const parts: string[] = [];
    
    // Try to extract original email from body first
    const originalEmail = this.extractOriginalEmailFromBody(emailDetails.body);
    
    let fromEmail: string | undefined;
    let fromName: string | undefined;
    let toRecipients: { email: string; name: string }[] | undefined;

    if (originalEmail) {
      // Use original email's from/to if found
      fromEmail = originalEmail.from;
      fromName = originalEmail.fromName;
      toRecipients = originalEmail.to;
    } else {
      // Fall back to current email's from/to
      fromEmail = emailDetails.from;
      fromName = emailDetails.fromName;
      toRecipients = emailDetails.to;
    }
    
    if (fromEmail) {
      const displayName = fromName ? `${fromName} ` : '';
      parts.push(`From: ${displayName}<${fromEmail}>`);
    }
    
    if (toRecipients && toRecipients.length > 0) {
      const toList = toRecipients.map(t => t.name ? `${t.name} <${t.email}>` : t.email).join(', ');
      parts.push(`To: ${toList}`);
    }
    
    if (emailDetails.subject) {
      parts.push(`Subject: ${emailDetails.subject}`);
    }
    
    return parts.join('\n');
  }

  static async authenticateAndCreateItem(
    appId: number,
    appToken: string,
    emailDetails: EmailDetails,
    emailBlob: Blob
  ) {
    try {
      const auth = await Podio.auth(this.appCredentials).appAuth(appId, appToken);

      let fromContact: number | null = null;
      let toContacts: number[] = [];
      let ccBccContacts: number[] = []; // Combined CC and BCC contacts

      // Get from contact
      if (emailDetails?.from) {
        fromContact = await this.getOrCreateContact(emailDetails.from, emailDetails.fromName);
      }

      // Get to contacts
      if (emailDetails?.to?.length) {
        toContacts = await Promise.all(
          emailDetails.to.map((to) => this.getOrCreateContact(to.email, to.name))
        );
      }

      // Get combined cc and bcc contacts
      const ccEmails = emailDetails?.cc?.map((cc) => cc.email) || [];
      const bccEmails = emailDetails?.bcc?.map((bcc) => bcc.email) || [];
      const allCcBccEmails = [...ccEmails, ...bccEmails];

      if (allCcBccEmails.length) {
        ccBccContacts = await Promise.all(
          allCcBccEmails.map((email) => this.getOrCreateContact(email))
        );
      }

      // Upload email as EML file
      const filename = this.sanitizeFilename(`${emailDetails.subject || "email"}.eml`);
      const uploadedEmailFile = await this.uploadFile(
        {
          emailBlob,
          filename,
          mimeType: "message/rfc822",
        },
        auth.access_token
      );

      console.log("Email file uploaded successfully", uploadedEmailFile);

      // Upload attachments
      const fileIds = [uploadedEmailFile.file_id];

      if (emailDetails.hasAttachments && Office.context.mailbox.item?.attachments) {
        const attachments = Office.context.mailbox.item.attachments;
        
        for (let i = 0; i < attachments.length; i++) {
          const attachment = attachments[i];
          try {
            const attachmentBlob = await this.getAttachmentContent(attachment);
            const attachmentFile = await this.uploadFile(
              {
                emailBlob: attachmentBlob,
                filename: this.sanitizeFilename(attachment.name),
                mimeType: this.getMimeTypeFromExtension(attachment.name),
              },
              auth.access_token
            );
            console.log(`Attachment ${attachment.name} uploaded successfully`, attachmentFile);
            fileIds.push(attachmentFile.file_id);
          } catch (error) {
            console.error(`Failed to upload attachment ${attachment.name}:`, error);
          }
        }
      }

      // Normalize internetMessageId to use as external_id
      const normalizedMessageId = this.normalizeInternetMessageId(emailDetails?.internetMessageId);
      
      const attributes = {
        fields: {
          title: emailDetails?.subject,
          from: fromContact,
          to: toContacts,
          cc: ccBccContacts,
          text: emailDetails?.body,
        },
        file_ids: fileIds,
        external_id: normalizedMessageId,
      };

      // Check if item with this external_id already exists
      let existingItem = null;
      const externalId = normalizedMessageId;
      if (externalId) {
        existingItem = await this.getItemByExternalId(appId, externalId, auth);
      }

      let item;
      let wasUpdated = false;
      let itemLink: string | undefined;
      
      if (existingItem) {
        // Update existing item
        console.log(`Updating existing item ${existingItem.item_id} with external_id: ${externalId}`);
        item = await Podio.api.item(auth).update(existingItem.item_id, attributes, true, false);
       
        if (item && (item.status === 200 || item.statusCode === 200 || !item.error)) {
          console.log("Item updated successfully with email file and attachments");
          wasUpdated = true;
         
          const itemIdToFetch = item.data?.item_id || existingItem.item_id;
          const updatedItem = await this.getItemById(itemIdToFetch, auth);
          debugger;
          if (updatedItem) {
            itemLink = updatedItem.link;
          } else {
            // Fallback: construct link from item_id if fetch fails
            itemLink = `https://podio.com/item/${itemIdToFetch}`;
          }
        } else {
          // Update failed, throw error
          throw new Error(`Failed to update item: ${item?.error || 'Unknown error'}`);
        }
      } else {
        // Create new item
        console.log(`Creating new item with external_id: ${externalId}`);
        item = await Podio.api.item(auth).create(appId, attributes, true, true);
        console.log("Item created successfully with email file and attachments");
        wasUpdated = false;
        itemLink = item.data.link;
        
        // Add comment with original email information (From, To, Subject)
        // This preserves the original email details even when replies override them
        const itemId = item.data?.item_id;
        if (itemId) {
          const commentText = this.formatOriginalEmailComment(emailDetails);
          await this.addCommentToItem(itemId, commentText, auth);
        }
      }

      // Use existingItem.item_id if item.data.item_id is null (for updates)
      const finalItemId = item.data?.item_id || (existingItem?.item_id);
      const finalLink = itemLink || item.data?.link || `https://podio.com/item/${finalItemId}`;
      
      return {
        itemId: finalItemId,
        link: finalLink,
        wasUpdated: wasUpdated,
      };
    } catch (error) {
      console.error("Error updating Podio item:", error.message);
      throw error;
    }
  }

  private static async getOrCreateContact(email: string, name?: string): Promise<number> {
    const appId = 29587342;
    const appToken = "2df595b795bf00d8e4de57ff9751f8ee";
    const auth = await Podio.auth(this.appCredentials).appAuth(appId, appToken);

    // First try to find existing contact
    console.log("Searching for contact with email:", email);
    const filter = await podio.api.item(auth).filter(appId, {
      filters: {
        "email-address": email,
      },
    });

    console.log("Filter response:", filter.data.items);

    // If contact exists, return its ID
    if (filter.data.items.length > 0) {
      return filter.data.items[0].item_id;
    }

    // If contact doesn't exist, create new one
    try {
      const newContact = await Podio.api.item(auth).create(appId, {
        fields: {
          "email-address": email,
          "title": name, // Use part before @ as name
          // Add other required fields here
        },
      });

      console.log("Created new contact:", newContact);
      if (newContact.data.item_id != null || newContact.data.item_id != 0) {
        return newContact.data.item_id;
      } else {
        return newContact.data.app_item_id;
      }
    } catch (error) {
      console.error("Error creating contact:", error);
      throw new Error(`Failed to create contact for email: ${email}`);
    }
  }
}
